import { MessageCircle, Check, Package, ArrowLeft, ArrowRight, X, Gift, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import { useLanguage } from "../i18n/LanguageContext";
import { Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import { useState, useEffect } from "react";
import { CouponPriceDisplay } from "../components/CouponPriceDisplay";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WHATSAPP_NUMBER = "972528614433";
const PRODUCT_IMAGE = "/images/product-main.jpg";
const MINI_TINS_IMAGE = "/images/mini-tins.jpg";

export const Product = () => {
  const { t, language } = useLanguage();
  const Arrow = language === 'he' ? ArrowLeft : ArrowRight;
  const isRTL = language === 'he';
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(PRODUCT_IMAGE);
  const [selectedProduct, setSelectedProduct] = useState('single'); // 'single' or 'pack5'
  const [activeCoupon, setActiveCoupon] = useState(null);
  const [user, setUser] = useState(null);

  // Check if user is logged in
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const storedToken = localStorage.getItem('mart_session_token');
        if (!storedToken) return;
        
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: "include",
          headers: {
            'Authorization': `Bearer ${storedToken}`
          },
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        }
      } catch (error) {
        console.log("Not authenticated");
      }
    };
    checkAuth();
  }, []);

  // Mark coupon as used when clicking WhatsApp
  const handleWhatsAppClick = async () => {
    if (activeCoupon) {
      try {
        const storedToken = localStorage.getItem('mart_session_token');
        await fetch(`${BACKEND_URL}/api/coupons/mark-used`, {
          method: 'POST',
          credentials: "include",
          headers: {
            'Authorization': `Bearer ${storedToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            code: activeCoupon.code,
            product: selectedProduct
          })
        });
      } catch (error) {
        console.log("Error marking coupon used:", error);
      }
    }
  };
  
  // Create WhatsApp message with coupon if applicable
  const getWhatsAppUrl = () => {
    let message;
    if (selectedProduct === 'single') {
      message = activeCoupon 
        ? language === 'he'
          ? `היי! אשמח להזמין דאודורנט טבעי 50 גרם 🌿\nקוד קופון: ${activeCoupon.code} (${activeCoupon.discount}% הנחה)`
          : `Hi! I'd like to order a natural deodorant 50g 🌿\nCoupon code: ${activeCoupon.code} (${activeCoupon.discount}% discount)`
        : t.product.whatsappMessage;
    } else {
      message = activeCoupon
        ? language === 'he'
          ? `היי! אשמח להזמין מארז 5 אריזות (10 גרם כל אחת) + כפיות במבוק 🌿\nקוד קופון: ${activeCoupon.code} (${activeCoupon.discount}% הנחה)`
          : `Hi! I'd like to order a 5-pack (10g each) + bamboo spoons 🌿\nCoupon code: ${activeCoupon.code} (${activeCoupon.discount}% discount)`
        : language === 'he' 
          ? "היי! אשמח להזמין מארז 5 אריזות (10 גרם כל אחת) + כפיות במבוק 🌿"
          : "Hi! I'd like to order a 5-pack (10g each) + bamboo spoons 🌿";
    }
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
  };

  const openLightbox = (image) => {
    setLightboxImage(image);
    setShowLightbox(true);
  };

  return (
    <div className="min-h-screen pt-24 pb-16">
      <SEO page="product" />
      
      {/* Lightbox */}
      {showLightbox && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowLightbox(false)}
        >
          <button 
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
            onClick={() => setShowLightbox(false)}
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={lightboxImage}
            alt={language === 'he' ? "דאודורנט טבעי M'ART" : "M'ART Natural Deodorant"}
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
          />
        </div>
      )}
      
      {/* Header */}
      <section className="px-4 sm:px-6 mb-8">
        <div className="max-w-3xl mx-auto text-center">
          <div className="decorative-line mb-4" />
          <h1 
            data-testid="product-title"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium text-[#2C3329] mb-4 animate-fade-in-up"
          >
            {t.product.title}
          </h1>
          <p className="text-lg text-[#6B7266] animate-fade-in-up animation-delay-200">
            {t.product.subtitle}
          </p>
        </div>
      </section>

      {/* Product Selection Tabs */}
      <section className="px-4 sm:px-6 mb-8">
        <div className="max-w-md mx-auto">
          <div className="flex rounded-xl bg-[#E8E4DC]/50 p-1 gap-1">
            <button
              onClick={() => setSelectedProduct('single')}
              className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                selectedProduct === 'single'
                  ? 'bg-white text-[#2C3329] shadow-sm'
                  : 'text-[#6B7266] hover:text-[#2C3329]'
              }`}
            >
              <div className="text-sm">{language === 'he' ? 'אריזה בודדת' : 'Single'}</div>
              <div className="text-xs opacity-70">50 {language === 'he' ? 'גרם' : 'g'}</div>
            </button>
            <button
              onClick={() => setSelectedProduct('pack5')}
              className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all relative ${
                selectedProduct === 'pack5'
                  ? 'bg-white text-[#2C3329] shadow-sm'
                  : 'text-[#6B7266] hover:text-[#2C3329]'
              }`}
            >
              <span className="absolute -top-2 right-2 text-[10px] bg-amber-500 text-white px-2 py-0.5 rounded-full">
                {language === 'he' ? 'לשתף!' : 'Share!'}
              </span>
              <div className="text-sm">{language === 'he' ? 'מארז קהילה' : 'Community Pack'}</div>
              <div className="text-xs opacity-70">5×10 {language === 'he' ? 'גרם' : 'g'}</div>
            </button>
          </div>
        </div>
      </section>

      {/* Product Card - Single */}
      {selectedProduct === 'single' && (
        <section className="px-4 sm:px-6 mb-12">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Product Image with Lightbox */}
              <div 
                className="image-premium aspect-square rounded-2xl overflow-hidden animate-fade-in-up cursor-pointer group relative"
                onClick={() => openLightbox(PRODUCT_IMAGE)}
              >
                <img
                  src={PRODUCT_IMAGE}
                  alt={language === 'he' ? "דאודורנט טבעי M'ART" : "M'ART Natural Deodorant"}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  data-testid="product-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                  <p className="text-white/70 text-sm text-center">
                    {language === 'he' ? 'לחצו להגדלה' : 'Click to enlarge'}
                  </p>
                </div>
              </div>

              {/* Product Info */}
              <div className="space-y-6 animate-fade-in-up animation-delay-200">
                {/* Main Description */}
                <div className="space-y-4">
                  <p className="text-[#2C3329] dark:text-[#E8E4DC] leading-relaxed text-lg">
                    {language === 'he' 
                      ? <>אם אין לכם זמן או חשק להכין לבד(<span className="italic">*עדיין*</span>), אפשר פשוט לקנות.</>
                      : <>If you don&apos;t have time or desire to make it yourself(<span className="italic">*yet*</span>), you can simply buy.</>
                    }
                  </p>
                  <p className="text-[#6B7266] dark:text-[#9A9790] leading-relaxed">
                    {language === 'he' 
                      ? 'זה בדיוק אותו מוצר, אותם מרכיבים ואותו תהליך שאנחנו עושים במעבדה שלנו, רק ארוז ומוכן לשימוש.'
                      : 'It\'s exactly the same product, same ingredients and same process we do in our lab, just packaged and ready to use.'
                    }
                  </p>
                </div>

                {/* Highlight Box */}
                <div className="bg-gradient-to-br from-[#4A5D43]/5 to-[#4A5D43]/10 dark:from-[#4A5D43]/10 dark:to-[#4A5D43]/20 rounded-xl p-4 border border-[#4A5D43]/20">
                  <p className="text-[#4A5D43] dark:text-[#7DAA6B] font-medium text-center">
                    {language === 'he' ? 'המארז הזה מעולה לטווח ארוך.' : 'This package is great for the long term.'}
                  </p>
                </div>

                {/* Price with Coupon Display */}
                <div className="flex flex-col items-center">
                  {user ? (
                    <CouponPriceDisplay
                      originalPrice={49}
                      currency="₪"
                      product="single"
                      language={language}
                      onCouponApplied={setActiveCoupon}
                    />
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-baseline gap-2 justify-center">
                        <span className="font-serif text-5xl font-medium text-[#2C3329] dark:text-white">
                          49 ₪
                        </span>
                        <span className="text-[#6B7266] dark:text-[#9A9790] text-lg">/ 50 {language === 'he' ? 'מ״ל' : 'ml'}</span>
                      </div>
                      
                      {/* Google Users Note - for non-logged users */}
                      <div className="flex items-start gap-3 p-3 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 rounded-xl border border-blue-100 dark:border-blue-800/30">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-green-500 flex items-center justify-center">
                          <svg className="h-4 w-4 text-white" viewBox="0 0 24 24">
                            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-[#2C3329] dark:text-white">
                            {language === 'he' ? 'קופון למשתמשי גוגל' : 'Google Users Coupon'}
                          </p>
                          <p className="text-xs text-[#6B7266] dark:text-[#9A9790] mt-0.5">
                            {language === 'he' 
                              ? 'התחברו עם גוגל והקלידו GOOGLE20 לקבלת 20% הנחה!' 
                              : 'Sign in with Google and enter GOOGLE20 for 20% off!'
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* What's Included */}
                <div className="space-y-3">
                  {[
                    language === 'he' ? 'צנצנת זכוכית ענברית' : 'Amber glass jar',
                    language === 'he' ? 'כפיות עץ במבוק' : 'Bamboo wooden spoons',
                    language === 'he' ? 'הוראות שימוש ורכיבים' : 'Usage instructions and ingredients',
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full bg-[#4A5D43]/10 flex items-center justify-center">
                        <Check className="h-4 w-4 text-[#4A5D43]" />
                      </div>
                      <span className="text-[#2C3329] dark:text-[#E8E4DC]">{item}</span>
                    </div>
                  ))}
                </div>

                {/* WhatsApp CTA */}
                <div className="space-y-3">
                  <a 
                    href={getWhatsAppUrl()} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="block"
                    onClick={handleWhatsAppClick}
                  >
                    <Button 
                      data-testid="whatsapp-buy-btn"
                      className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white text-lg py-7 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      <MessageCircle className={`${isRTL ? 'ml-3' : 'mr-3'} h-5 w-5`} />
                      {activeCoupon 
                        ? (language === 'he' ? `להזמנה עם ${activeCoupon.discount}% הנחה` : `Order with ${activeCoupon.discount}% off`)
                        : t.product.cta
                      }
                    </Button>
                  </a>
                  
                  {/* Google Pay - Coming Soon */}
                  <div className="relative">
                    <Button 
                      disabled
                      data-testid="google-pay-single-btn"
                      className="w-full bg-white hover:bg-gray-50 text-gray-800 border border-gray-300 text-lg py-7 rounded-xl shadow-sm transition-all duration-300 opacity-75 cursor-not-allowed"
                    >
                      <svg className={`${isRTL ? 'ml-3' : 'mr-3'} h-6 w-6`} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z" fill="#4285F4"/>
                      </svg>
                      <span className="font-medium">Google Pay</span>
                    </Button>
                    {/* Coming Soon Badge */}
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold rounded-full shadow-md">
                      {language === 'he' ? 'בקרוב' : 'Coming Soon'}
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-center gap-2 pt-2">
                    <div className="flex items-center gap-2 text-sm text-[#6B7266] dark:text-[#9A9790]">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span>{language === 'he' ? 'זמין עכשיו' : 'Available now'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Product Card - 5 Pack */}
      {selectedProduct === 'pack5' && (
        <section className="px-4 sm:px-6 mb-12">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Product Image */}
              <div 
                className="image-premium aspect-[4/3] rounded-2xl overflow-hidden animate-fade-in-up cursor-pointer group relative"
                onClick={() => openLightbox(MINI_TINS_IMAGE)}
              >
                <img
                  src={MINI_TINS_IMAGE}
                  alt={language === 'he' ? "מארז 5 אריזות M'ART" : "M'ART 5-Pack"}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  data-testid="pack5-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                  <p className="text-white/70 text-sm text-center">
                    {language === 'he' ? 'לחצו להגדלה' : 'Click to enlarge'}
                  </p>
                </div>
              </div>

              {/* Product Info */}
              <div className="space-y-6 animate-fade-in-up animation-delay-200">
                <div>
                  <h2 className="font-serif text-2xl font-medium text-[#2C3329] mb-3">
                    {language === 'he' ? 'מארז קהילה' : 'Community Pack'}
                  </h2>
                  <p className="text-[#6B7266] leading-relaxed text-lg">
                    {language === 'he' 
                      ? '5 אריזות מתכת קטנות ואלגנטיות של 10 גרם כל אחת. מושלם לחלוק עם משפחה וחברים. כולל כפיות במבוק קטנות למריחה נוחה. האריזות מגיעות בשקית זיפ לוק שקופה ואטומה למניעת תקלות טכניות :) אהבת את הרעיון והיוזמה? רכוש את המארז ושתף את הקרובים שלך.'
                      : '5 elegant small metal tins of 10g each. Perfect for sharing with family and friends. Includes small bamboo spoons for easy application. Tins come in a sealed transparent ziplock bag. Loved the idea? Get the pack and share with your loved ones.'
                    }
                  </p>
                </div>

                {/* Price with Coupon Display */}
                <div className="flex flex-col items-center">
                  {user ? (
                    <CouponPriceDisplay
                      originalPrice={50}
                      currency="₪"
                      product="pack5"
                      language={language}
                      onCouponApplied={setActiveCoupon}
                    />
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-baseline gap-3">
                        <span className="font-serif text-5xl font-medium text-[#2C3329]">
                          50 ₪
                        </span>
                        <span className="text-[#6B7266]">/ 5 × 10 {language === 'he' ? 'גרם' : 'g'}</span>
                      </div>
                      
                      {/* Google Users Note - for non-logged users */}
                      <div className="flex items-start gap-3 p-3 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 rounded-xl border border-blue-100 dark:border-blue-800/30">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-green-500 flex items-center justify-center">
                          <svg className="h-4 w-4 text-white" viewBox="0 0 24 24">
                            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-[#2C3329] dark:text-white">
                            {language === 'he' ? 'קופון למשתמשי גוגל' : 'Google Users Coupon'}
                          </p>
                          <p className="text-xs text-[#6B7266] dark:text-[#9A9790] mt-0.5">
                            {language === 'he' 
                              ? 'התחברו עם גוגל והקלידו GOOGLE20 לקבלת 20% הנחה!' 
                              : 'Sign in with Google and enter GOOGLE20 for 20% off!'
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* What's Included */}
                <div className="space-y-3">
                  {[
                    language === 'he' ? '5 אריזות מתכת אלגנטיות' : '5 elegant metal tins',
                    language === 'he' ? '10 גרם דאודורנט בכל אריזה' : '10g deodorant in each tin',
                    language === 'he' ? 'כפיות במבוק למריחה' : 'Bamboo spoons for application',
                    language === 'he' ? 'מושלם למתנות ושיתוף' : 'Perfect for gifts and sharing',
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                        <Check className="h-4 w-4 text-amber-600" />
                      </div>
                      <span className="text-[#2C3329]">{item}</span>
                    </div>
                  ))}
                </div>

                {/* WhatsApp CTA */}
                <div className="space-y-3">
                  <a 
                    href={getWhatsAppUrl()} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="block"
                    onClick={handleWhatsAppClick}
                  >
                    <Button 
                      data-testid="whatsapp-pack5-btn"
                      className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white text-lg py-7 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      <MessageCircle className={`${isRTL ? 'ml-3' : 'mr-3'} h-5 w-5`} />
                      {activeCoupon 
                        ? (language === 'he' ? `להזמנת מארז 5 עם ${activeCoupon.discount}% הנחה` : `Order 5-Pack with ${activeCoupon.discount}% off`)
                        : (language === 'he' ? 'להזמנת מארז 5 בוואטסאפ' : 'Order 5-Pack on WhatsApp')
                      }
                    </Button>
                  </a>
                  
                  {/* Google Pay - Coming Soon */}
                  <div className="relative">
                    <Button 
                      disabled
                      data-testid="google-pay-pack5-btn"
                      className="w-full bg-white hover:bg-gray-50 text-gray-800 border border-gray-300 text-lg py-7 rounded-xl shadow-sm transition-all duration-300 opacity-75 cursor-not-allowed"
                    >
                      <svg className={`${isRTL ? 'ml-3' : 'mr-3'} h-6 w-6`} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z" fill="#4285F4"/>
                      </svg>
                      <span className="font-medium">Google Pay</span>
                    </Button>
                    {/* Coming Soon Badge */}
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold rounded-full shadow-md">
                      {language === 'he' ? 'בקרוב' : 'Coming Soon'}
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-center gap-2 pt-2">
                    <div className="flex items-center gap-2 text-sm text-[#6B7266]">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span>{language === 'he' ? 'זמין עכשיו' : 'Available now'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* DIY Kit Teaser */}
      <section className="px-4 sm:px-6 mb-10">
        <div className="max-w-2xl mx-auto">
          <Link to="/diy-kit">
            <div className="premium-card rounded-2xl p-6 hover:shadow-lg transition-all border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white group cursor-pointer">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                  <Gift className="h-7 w-7 text-amber-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-serif text-lg text-[#2C3329]">
                      {language === 'he' ? 'ערכה להכנה עצמית' : 'DIY Kit'}
                    </h3>
                    <span className="text-[10px] bg-amber-500 text-white px-2 py-0.5 rounded-full font-medium">
                      {language === 'he' ? 'חדש!' : 'New!'}
                    </span>
                  </div>
                  <p className="text-sm text-[#6B7266]">
                    {language === 'he' 
                      ? 'כל החומרים והכלים להכנת 100 גרם דאודורנט בבית, 90 ₪'
                      : 'All materials and tools to make 100g of deodorant at home, 90 ₪'
                    }
                  </p>
                </div>
                <Arrow className="h-5 w-5 text-amber-600 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Alternative Path */}
      <section className="px-4 sm:px-6 py-10 section-warm">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#2C3329]/5 flex items-center justify-center mx-auto mb-4">
            <Package className="h-7 w-7 text-[#2C3329]/60" />
          </div>
          <h2 className="font-serif text-xl text-[#2C3329] mb-3">
            {language === 'he' ? 'מעדיפים להכין לבד?' : 'Prefer to make it yourself?'}
          </h2>
          <p className="text-[#6B7266] mb-6">
            {language === 'he' 
              ? 'כל המידע פתוח וזמין בחינם. לא צריך לקנות כדי לדעת איך להכין.'
              : 'All information is open and available for free. No purchase required to know how to make it.'
            }
          </p>
          <Link to="/diy">
            <Button 
              variant="outline"
              data-testid="go-to-diy-btn"
              className="border-[#4A5D43] text-[#4A5D43] hover:bg-[#4A5D43] hover:text-white rounded-full px-8 py-5"
            >
              {language === 'he' ? 'לעמוד ההכנה העצמית' : 'Go to DIY page'}
              <Arrow className={`${language === 'he' ? 'mr-2' : 'ml-2'} h-4 w-4`} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Product;
